import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { formatDateTime } from '../lib/format';

interface AuditEntry {
  id: number;
  actor_user_id: string;
  actor_display_name: string | null;
  actor_username: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  entity_label: string | null;
  changes: Record<string, unknown> | null;
  created_at: string;
}

/**
 * Audit-trail list scoped to one entity — the Activity tab of each detail
 * page. Reads GET /admin/accounts/audit-log filtered by entity.
 */
const EntityAuditList: React.FC<{ entityType: string; entityId: string }> = ({
  entityType,
  entityId,
}) => {
  const auditQuery = useQuery({
    queryKey: ['admin-accounts', 'audit', entityType, entityId],
    queryFn: async () => {
      const response = await api.get('/admin/accounts/audit-log', {
        params: { entity_type: entityType, entity_id: entityId, limit: 50 },
      });
      return response.data.data.entries as AuditEntry[];
    },
  });

  if (auditQuery.isLoading) {
    return <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading activity…</p>;
  }
  if (auditQuery.isError) {
    return <p className="text-sm text-error-600 dark:text-error-400">Couldn't load activity.</p>;
  }
  const entries = auditQuery.data ?? [];
  if (entries.length === 0) {
    return (
      <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
        No admin actions recorded for this record yet. (Audit logging starts with the new Accounts tool.)
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {entries.map((entry) => (
        <li key={entry.id} className="card p-3 text-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium text-text-primary dark:text-text-dark-primary">
              {entry.action}
            </span>
            <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              {formatDateTime(entry.created_at)}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-text-secondary dark:text-text-dark-secondary">
            by {entry.actor_display_name || entry.actor_username || entry.actor_user_id}
            {entry.changes && (
              <span className="ml-2 font-mono break-all">{JSON.stringify(entry.changes)}</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

export default EntityAuditList;
